// The call co-pilot's client (module 13; milestone 11 checks 3 and 7).
//
// MIRROR OF docs/MILESTONE-11.md "The contract, fixed before the tracks start", field for field.
// A copy rather than an import because the portal and the API do not depend on one another. Its
// own json helpers, as meetings/api.ts has, so the portal's api.ts (another track's file) is not
// edited; only the two error classes come from it, so a refusal reads the same on every page.
import { viewFetch as fetch } from '../view-as.js';
import { ApiError, Unauthenticated } from '../api.js';

export type CardKind = 'answer' | 'notes' | 'fact' | 'warning';
export type CardOutcome = 'used' | 'dismissed';

export interface CopilotSource { kind: 'call' | 'email' | 'teams' | 'other'; label: string; at: string | null; signalId: string }

export interface CopilotCard {
  id: string;
  kind: CardKind;
  shownAt: string;
  outcome: CardOutcome | null;
  /** The words that raised the card, on the push only; a re-read never carries them (the live stream is not filed, L8). */
  heard?: string | null;
  reply?: { entryId: string; body: string; playbook: string; version: number; approvedBy: string | null; approvedAt: string | null };
  objection?: { entryId: string; body: string };
  lines?: Array<{ text: string; source: CopilotSource; otherClient: string | null }>;
  fact?: { text: string; source: string };
  rule?: { entryId: string; body: string };
}

export type BotState = 'scheduled' | 'joining' | 'waiting' | 'in_call' | 'recording' | 'refused' | 'ended';

export interface CopilotCall {
  botId: string;
  company: { id: string; name: string } | null;
  /** The calendar invite the call fulfils, when it has one: the rundown reads who is in the room from it (pipeline RC5). */
  event?: string | null;
  meetingTitle: string | null;
  state: BotState;
  subCode: string | null;
  startsAt: string;
  listening: boolean;
  limit: null | 'call' | 'day';
  recordingLocally: boolean;
  /** Someone on the call is sharing a screen now, as the server last heard (so a share missed while the socket was down still hides the panel). */
  sharing?: boolean;
}

/** GET /api/copilot/call: the panel's first read and every reconnect's. */
export interface CopilotCallView { enabled: boolean; call: CopilotCall | null; cards: CopilotCard[] }

export type EntryKind = 'objection' | 'reply' | 'rule' | 'note';
export type EntryStatus = 'draft' | 'gap' | 'open' | 'approved' | 'retired';

export interface Entry {
  id: string;
  kind: EntryKind;
  parentId: string | null;
  ordinal: number;
  body: string;
  status: EntryStatus;
  source: string | null;
  approvedBy: string | null;
  approvedAt: string | null;
  version: number;
}

export interface Proposal {
  id: string;
  kind: 'new_objection' | 'new_reply' | 'fill_gap';
  targetEntryId: string | null;
  body: string;
  source: { signalId: string | null; label: string; atMinute: string | null };
  status: 'open' | 'approved' | 'rejected';
}

export interface Playbook { id: string; product: string; title: string; entries: Entry[]; proposals: Proposal[] }

/** GET /api/copilot/playbooks. */
export interface PlaybooksView { canApprove: boolean; approvers: string[]; playbooks: Playbook[] }

/** POST /api/copilot/playbooks/import. */
export interface ImportResult { playbookId: string; entries: number; gaps: number; open: number }

/** POST /api/copilot/playbooks: an empty playbook for a product (added to the contract by the main session, 2026-09-18). */
export interface NewPlaybook { id: string; product: string; title: string }

/** The one event type the co-pilot puts on the messenger's hub. */
export type CopilotEvent =
  | { type: 'copilot'; kind: 'card'; card: CopilotCard }
  | { type: 'copilot'; kind: 'bot'; botId: string; state: BotState; subCode: string | null }
  | { type: 'copilot'; kind: 'share'; on: boolean }
  | { type: 'copilot'; kind: 'call'; botId: string; state: 'listening' | 'ended' | 'limit'; limit?: 'call' | 'day' };

const CARD_KINDS: ReadonlySet<string> = new Set<CardKind>(['answer', 'notes', 'fact', 'warning']);
const BOT_STATES: ReadonlySet<string> = new Set<BotState>(['scheduled', 'joining', 'waiting', 'in_call', 'recording', 'refused', 'ended']);
const CALL_STATES: ReadonlySet<string> = new Set(['listening', 'ended', 'limit']);

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * A message off the messenger's socket, as a co-pilot event, or null when it is not one. The
 * socket carries the messenger's own events too, and a malformed push must not reach the panel's
 * state: a card without an id or a kind would draw as an empty box that cannot be answered.
 */
export function copilotEventOf(raw: unknown): CopilotEvent | null {
  if (!isObject(raw) || raw.type !== 'copilot') return null;
  if (raw.kind === 'card') {
    const c = raw.card;
    if (!isObject(c) || typeof c.id !== 'string' || typeof c.kind !== 'string' || !CARD_KINDS.has(c.kind) || typeof c.shownAt !== 'string') return null;
    return { type: 'copilot', kind: 'card', card: c as unknown as CopilotCard };
  }
  if (raw.kind === 'bot') {
    if (typeof raw.botId !== 'string' || typeof raw.state !== 'string' || !BOT_STATES.has(raw.state)) return null;
    return { type: 'copilot', kind: 'bot', botId: raw.botId, state: raw.state as BotState, subCode: typeof raw.subCode === 'string' ? raw.subCode : null };
  }
  if (raw.kind === 'share') {
    return typeof raw.on === 'boolean' ? { type: 'copilot', kind: 'share', on: raw.on } : null;
  }
  if (raw.kind === 'call') {
    if (typeof raw.botId !== 'string' || typeof raw.state !== 'string' || !CALL_STATES.has(raw.state)) return null;
    const state = raw.state as 'listening' | 'ended' | 'limit';
    if (raw.limit === 'call' || raw.limit === 'day') return { type: 'copilot', kind: 'call', botId: raw.botId, state, limit: raw.limit };
    return { type: 'copilot', kind: 'call', botId: raw.botId, state };
  }
  return null;
}

async function json<T>(res: Response): Promise<T> {
  if (res.status === 401) throw new Unauthenticated();
  if (!res.ok) {
    const said = await res.json()
      .then((b: unknown) => (isObject(b) && typeof b.error === 'string' ? b.error : null))
      .catch(() => null);
    throw new ApiError(res.status, said ?? `${res.status} ${res.statusText}`);
  }
  return (await res.json()) as T;
}

const get = <T>(path: string): Promise<T> => fetch(path, { credentials: 'same-origin' }).then((r) => json<T>(r));
const post = <T>(path: string, body: unknown): Promise<T> =>
  fetch(path, { method: 'POST', credentials: 'same-origin', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }).then((r) => json<T>(r));
const id = (s: string) => encodeURIComponent(s);
const storeQuery = (store: string) => `?store=${encodeURIComponent(store)}`;

export const copilot = {
  call: (store: string) => get<CopilotCallView>(`/api/copilot/call${storeQuery(store)}`),
  outcome: (store: string, cardId: string, outcome: CardOutcome) => post<{ ok: true }>(`/api/copilot/cards/${id(cardId)}/outcome`, { store, outcome }),
  playbooks: (store: string) => get<PlaybooksView>(`/api/copilot/playbooks${storeQuery(store)}`),
  importDeck: (store: string, product: string, fileName: string, pptxBase64: string) =>
    post<ImportResult>('/api/copilot/playbooks/import', { store, product, fileName, pptxBase64 }),
  createPlaybook: (store: string, product: string, title: string) => post<NewPlaybook>('/api/copilot/playbooks', { store, product, title }),
  /** An approver's entry by hand, created as a draft: a reply or a note under its objection, an objection or a rule under none. */
  addEntry: (store: string, playbookId: string, kind: EntryKind, parentId: string | null, body: string) =>
    post<Entry>(`/api/copilot/playbooks/${id(playbookId)}/entries`, { store, kind, parentId, body }),
  approve: (store: string, entryId: string) => post<Entry>(`/api/copilot/entries/${id(entryId)}/approve`, { store }),
  edit: (store: string, entryId: string, body: string) => post<Entry>(`/api/copilot/entries/${id(entryId)}`, { store, body }),
  decide: (store: string, proposalId: string, decision: 'approve' | 'reject', body?: string) =>
    post<Proposal>(`/api/copilot/proposals/${id(proposalId)}/decide`, body === undefined ? { store, decision } : { store, decision, body }),
};

/** What a refusal says, in the API's own words when it gave any. */
export function refusalWords(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : String(e);
}
